/**
 * @fileoverview Left-side template canvas palette for forms, fields, and
 * data-bound widgets.
 * @module template-builder
 *
 * Responsibilities:
 * - Render the canvas-side Forms, Fields, and Data Widgets tabs.
 * - Provide drag/drop sources for form blocks, sections, fields, and widgets.
 * - Reserve and resize left-side layout space so the central canvas can
 *   recenter within the remaining available work area.
 *
 * Dependencies:
 * - @dnd-kit drag/drop primitives
 * - WidgetPalette for data-bound widget selection
 * - Template type definitions from shared ui-admin contracts
 *
 * Architecture:
 * - UI-only composition component; persistence is limited to local UI
 *   preferences such as selected tab and palette width.
 * - No API calls or Dexie writes are performed here.
 *
 * Notes:
 * - The resize gutter mirrors the right-side tool-window gutter so both side
 *   panels can expand into or contract away from the canvas consistently.
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import {
  ChevronRight, Pin, PinOff,
  FileText, FolderOpen, Plus, LayoutDashboard,
  GripVertical,
  Database, SquarePen, Cpu,
} from 'lucide-react';
// Section 2.4: user-saved Widget Library removed. Data-bound widget catalog
// (WidgetPalette + DATA_BOUND_WIDGET_REGISTRY) is kept and rendered from
// the "Data Widgets" tab.
import { useDraggable } from '@dnd-kit/core';
import { FIELD_TYPE_GROUPS, FIELD_TYPE_LABELS } from '../../types/templateTypes';
import type { FieldType, TemplateType, FormField } from '../../types/templateTypes';
import type { DataBoundWidgetDefinition } from '../../types/templateTypes';
import { WidgetPalette } from '../form-builder/WidgetPalette';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PALETTE_WIDTH_KEY = 'firesquirrel_canvas_palette_width';
const PALETTE_PINNED_KEY = 'firesquirrel_canvas_palette_pinned';
const PALETTE_TAB_KEY = 'firesquirrel_canvas_palette_tab';
const MIN_PALETTE_W = 200;
const MAX_PALETTE_W = 400;
const DEFAULT_PALETTE_W = 220;
const TAB_RAIL_W = 32;

type PaletteTab = 'forms' | 'fields' | 'data';

function loadTab(): PaletteTab {
  try {
    const v = localStorage.getItem(PALETTE_TAB_KEY);
    if (v === 'forms' || v === 'fields' || v === 'data') return v;
  } catch { /* ignore */ }
  return 'forms';
}
function saveTab(t: PaletteTab) {
  try { localStorage.setItem(PALETTE_TAB_KEY, t); } catch { /* ignore */ }
}

function loadPaletteW(): number {
  try {
    const v = localStorage.getItem(PALETTE_WIDTH_KEY);
    if (v) { const n = parseInt(v, 10); if (n >= MIN_PALETTE_W && n <= MAX_PALETTE_W) return n; }
  } catch { /* ignore */ }
  return DEFAULT_PALETTE_W;
}
function savePaletteW(w: number) {
  try { localStorage.setItem(PALETTE_WIDTH_KEY, String(Math.round(w))); } catch { /* ignore */ }
}
function loadPinned(): boolean {
  try { return localStorage.getItem(PALETTE_PINNED_KEY) !== 'false'; } catch { return true; }
}
function savePin(v: boolean) {
  try { localStorage.setItem(PALETTE_PINNED_KEY, String(v)); } catch { /* ignore */ }
}

/**
 * @component PaletteResizeHandle
 *
 * Purpose:
 * - Provides the visible right-edge drag gutter for resizing the left palette.
 *
 * Props:
 * - onResize: Receives the horizontal resize delta in pixels.
 *
 * Behavior:
 * - Expands the palette when dragged right and shrinks it when dragged left.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - Does not persist width directly; parent owns bounds and persistence.
 *
 * Performance:
 * - Uses refs for pointer tracking to avoid per-move render churn.
 */
function PaletteResizeHandle({ onResize }: { onResize: (delta: number) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const lastX = useRef(0);
  const [active, setActive] = useState(false);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    dragging.current = true;
    setActive(true);
    lastX.current = e.clientX;
    ref.current?.setPointerCapture?.(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging.current) return;
    onResize(e.clientX - lastX.current);
    lastX.current = e.clientX;
  }, [onResize]);

  const onPointerUp = useCallback(() => {
    dragging.current = false;
    setActive(false);
  }, []);

  return (
    <div
      ref={ref}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      aria-label="Resize field palette"
      title="Drag to resize field palette"
      className={`group relative z-20 flex h-full w-6 shrink-0 cursor-col-resize select-none items-center justify-center touch-none transition-all duration-150 ${
        active ? 'bg-orange-100/90 shadow-[0_0_0_1px_var(--brand-primary)]' : 'bg-orange-50/60 hover:bg-orange-100/80 hover:shadow-[0_0_0_1px_var(--brand-primary)]'
      }`}
    >
      <div
        className={`h-full w-1.5 rounded-full transition-all duration-150 ${
          active ? 'bg-(--brand-primary) shadow-md' : 'bg-orange-400 group-hover:bg-(--brand-primary)'
        }`}
      />
      <div className="absolute top-1/2 flex h-8 -translate-y-1/2 items-center gap-0.5 rounded-full bg-white/80 px-1 py-1 shadow-xs ring-1 ring-orange-200 transition-all duration-150 group-hover:ring-orange-400">
        <span className="h-5 w-0.5 rounded-full bg-orange-400 transition-colors duration-150 group-hover:bg-(--brand-primary)" />
        <span className="h-5 w-0.5 rounded-full bg-orange-400 transition-colors duration-150 group-hover:bg-(--brand-primary)" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DraggablePaletteItem — for field types
// ---------------------------------------------------------------------------

function DraggablePaletteItem({ type, onClick }: { type: FieldType; onClick: () => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `canvas-palette-field-${type}`,
    data: { source: 'canvasPalette', fieldType: type },
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-blue-50 hover:text-blue-700 cursor-grab select-none transition-colors rounded-sm mx-1 ${isDragging ? 'opacity-50' : ''}`}
    >
      <GripVertical size={10} className="text-gray-300 shrink-0" />
      <span className="flex-1">{FIELD_TYPE_LABELS[type]}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PaletteGroup — collapsible section
// ---------------------------------------------------------------------------

function SectionHeader({ label }: { label: string }) {
  return (
    <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-100">
      <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">{label}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FieldSubGroup — collapsible within Fields group
// ---------------------------------------------------------------------------

function FieldSubGroup({ label, types, onAddField }: { label: string; types: FieldType[]; onAddField: (t: FieldType) => void }) {
  const [open, setOpen] = useState(label === 'Basic');
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wide hover:bg-gray-50 transition-colors"
      >
        {label}
        <ChevronRight size={10} className={`transition-transform text-gray-400 ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && types.map((type) => (
        <DraggablePaletteItem key={type} type={type} onClick={() => onAddField(type)} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PaletteFormItems — draggable "New Form", "Header Form", "From Library"
// ---------------------------------------------------------------------------

function DraggableFormItem({
  id,
  label,
  sublabel,
  icon,
  iconBg,
  disabled,
  onClick,
}: {
  id: string;
  label: string;
  sublabel: string;
  icon: React.ReactNode;
  iconBg: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data: { source: 'canvasPaletteForm', formItemId: id },
    disabled,
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={!disabled ? onClick : undefined}
      className={`flex items-center gap-2 mx-1 my-0.5 px-2 py-1.5 rounded-lg border text-xs select-none transition-all ${
        disabled
          ? 'opacity-40 cursor-not-allowed bg-gray-50 border-gray-100'
          : isDragging
            ? 'opacity-50 bg-white border-gray-200 shadow-md cursor-grabbing'
            : 'bg-white border-gray-200 hover:border-(--brand-primary) hover:shadow-xs cursor-grab'
      }`}
    >
      <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${iconBg}`}>
        {icon}
      </div>
      <div className="min-w-0">
        <div className="font-medium text-gray-800 truncate">{label}</div>
        <div className="text-[9px] text-gray-400 truncate">{sublabel}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PaletteSectionItem — draggable "New Section"
// ---------------------------------------------------------------------------

function DraggableSectionItem({ onClick }: { onClick: () => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: 'canvas-palette-new-section',
    data: { source: 'canvasPaletteSection' },
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={onClick}
      className={`flex items-center gap-2 mx-1 my-0.5 px-2 py-1.5 rounded-lg border text-xs select-none transition-all ${
        isDragging
          ? 'opacity-50 bg-white border-gray-200 shadow-md cursor-grabbing'
          : 'bg-white border-gray-200 hover:border-purple-400 hover:shadow-xs cursor-grab'
      }`}
    >
      <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0 bg-white border border-purple-100">
        <LayoutDashboard size={12} className="text-purple-600" />
      </div>
      <div className="min-w-0">
        <div className="font-medium text-gray-800">New Section</div>
        <div className="text-[9px] text-gray-400">Drop onto an expanded form</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TabRail — permanent left tab strip
// ---------------------------------------------------------------------------

const TABS: { id: PaletteTab; icon: React.ReactNode; label: string }[] = [
  { id: 'forms',  icon: <FileText size={13} />,   label: 'Forms'  },
  { id: 'fields', icon: <SquarePen size={13} />,  label: 'Fields' },
  { id: 'data',   icon: <Database size={13} />,   label: 'Data'   },
];

function TabRail({ active, onChange }: { active: PaletteTab; onChange: (t: PaletteTab) => void }) {
  return (
    <div
      className="w-8 shrink-0 flex flex-col items-center pt-3 gap-1 bg-[#f3f4f6] border-r border-gray-200"
    >
      {TABS.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          title={t.label}
          className={`w-7 flex flex-col items-center gap-0.5 py-2 rounded transition-colors ${
            active === t.id
              ? 'bg-white text-(--brand-primary) shadow-xs ring-1 ring-gray-200'
              : 'text-gray-400 hover:text-gray-700 hover:bg-white'
          }`}
        >
          {t.icon}
          <span
            className="text-[8px] font-semibold tracking-wide leading-none [writing-mode:vertical-rl] rotate-180"
          >
            {t.label}
          </span>
        </button>
      ))}
    </div>
  );
}

// Section 2.4: DraggableWidgetItem + SavedWidgetLibraryGroup removed.
// The user-saved Widget Library feature is gone — admins configure fields
// via the pre-built Data Widgets catalog (DATA_BOUND_WIDGET_REGISTRY)
// rendered by WidgetPalette in the "Data Widgets" tab.

// ---------------------------------------------------------------------------
// CanvasFieldPalette — Master Component Tray
// ---------------------------------------------------------------------------

export type PaletteSelectionContext = 'none' | 'form' | 'section' | 'field';

/**
 * @contract CanvasFieldPaletteProps
 *
 * Purpose:
 * - Defines the data and callbacks required by the left canvas palette.
 *
 * Fields:
 * - templateType: Current template category used to filter widgets.
 * - activeFormFields: Fields from the currently selected form.
 * - activeFormName: Display name for the current add-target form.
 * - hasHeaderForm: Whether a header form already exists.
 * - selectedFormIsModule: Whether selected form is a locked system module.
 * - selectedFormIsHeader: Whether selected form is a rich-text header form.
 * - existingModuleTypes: Module types already present on the template.
 * - selectionContext: Current canvas selection scope.
 * - onAddField: Adds a field to the active form/section.
 * - onAddWidget: Adds one data-bound widget.
 * - onAddWidgets: Adds multiple data-bound widgets.
 * - onAddNewForm: Adds a blank form.
 * - onAddFromLibrary: Opens reusable form selection.
 * - onAddHeaderForm: Adds a header/instructions form.
 * - onAddSection: Adds a section to the active form.
 * - onAddModule: Opens system module selection.
 * - onClose: Optional close callback retained for host compatibility.
 *
 * Rules:
 * - Callbacks are UI events only; persistence is owned by the host canvas.
 *
 * Sync:
 * - Local-first sync is host-managed through template/form callbacks.
 */
export interface CanvasFieldPaletteProps {
  templateType: TemplateType;
  activeFormFields: FormField[];
  activeFormName: string | null;
  hasHeaderForm: boolean;
  selectedFormIsModule?: boolean;
  selectedFormIsHeader?: boolean;
  existingModuleTypes?: Set<string>;
  selectionContext?: PaletteSelectionContext;
  onAddField: (type: FieldType) => void;
  onAddWidget: (widget: DataBoundWidgetDefinition) => void;
  onAddWidgets: (widgets: DataBoundWidgetDefinition[]) => void;
  onAddNewForm: () => void;
  onAddFromLibrary: () => void;
  onAddHeaderForm: () => void;
  onAddSection: () => void;
  onAddModule: () => void;
  onClose?: () => void;
  /**
   * When false, the form-adding palette items (New Form, From Library, Header
   * Form, System Module) render disabled — every one of them is a
   * create-then-attach path. Defaults to true.
   */
  canCreateForms?: boolean;
}

export function CanvasFieldPalette({
  templateType,
  activeFormFields,
  activeFormName,
  hasHeaderForm,
  selectedFormIsModule = false,
  selectedFormIsHeader = false,
  existingModuleTypes = new Set(),
  selectionContext = 'none',
  onAddField,
  onAddWidget,
  onAddWidgets,
  onAddNewForm,
  onAddFromLibrary,
  onAddHeaderForm,
  onAddSection,
  onAddModule,
  canCreateForms = true,
}: CanvasFieldPaletteProps) {
  const [pinned, setPinned] = useState(loadPinned);
  const [width, setWidth] = useState(loadPaletteW);
  const [activeTab, setActiveTab] = useState<PaletteTab>(loadTab);

  // Auto-switch tab when selection context changes:
  // - Selecting a form → stay on Forms tab (user may want to add sections)
  // - Selecting a section or field → switch to Fields tab (most likely next action)
  useEffect(() => {
    if (selectionContext === 'section' || selectionContext === 'field') {
      setActiveTab((prev) => {
        const next: PaletteTab = 'fields';
        if (prev !== next) saveTab(next);
        return next;
      });
      setPanelOpen(true);
    } else if (selectionContext === 'none') {
      setActiveTab((prev) => {
        const next: PaletteTab = 'forms';
        if (prev !== next) saveTab(next);
        return next;
      });
    }
  }, [selectionContext]);
  const [panelOpen, setPanelOpen] = useState(true);
  const handleTogglePin = useCallback(() => {
    setPinned((v) => { savePin(!v); return !v; });
  }, []);

  const handleTabClick = useCallback((tab: PaletteTab) => {
    if (activeTab === tab && panelOpen) {
      setPanelOpen(false);
    } else {
      setActiveTab(tab);
      saveTab(tab);
      setPanelOpen(true);
    }
  }, [activeTab, panelOpen]);

  const handleResize = useCallback((delta: number) => {
    setWidth((w) => {
      const next = Math.min(MAX_PALETTE_W, Math.max(MIN_PALETTE_W, w + delta));
      savePaletteW(next);
      return next;
    });
  }, []);

  // ── Tab panel content ──
  const formsPanel = (
    <div className="flex-1 overflow-y-auto">
      <SectionHeader label="Forms" />
      <div className="py-1">
        <DraggableFormItem
          id="palette-new-form"
          label="New Form"
          sublabel="Blank form with sections"
          icon={<Plus size={12} className="text-blue-600" />}
          iconBg="bg-white border border-blue-100"
          disabled={!canCreateForms}
          onClick={onAddNewForm}
        />
        <DraggableFormItem
          id="palette-from-library"
          label="From Library"
          sublabel="Pick a reusable form"
          icon={<FolderOpen size={12} className="text-violet-600" />}
          iconBg="bg-white border border-violet-100"
          disabled={!canCreateForms}
          onClick={onAddFromLibrary}
        />
        {!hasHeaderForm && (
          <DraggableFormItem
            id="palette-header-form"
            label="Header Form"
            sublabel="Rich text instructions"
            icon={<FileText size={12} className="text-amber-600" />}
            iconBg="bg-white border border-amber-100"
            disabled={!canCreateForms}
            onClick={onAddHeaderForm}
          />
        )}
        {(!existingModuleTypes.has('contacts') || !existingModuleTypes.has('violations')) && (
          <DraggableFormItem
            id="palette-system-module"
            label="System Module"
            sublabel={
              existingModuleTypes.has('contacts') && !existingModuleTypes.has('violations')
                ? 'Add: Violations'
                : !existingModuleTypes.has('contacts') && existingModuleTypes.has('violations')
                  ? 'Add: Contacts'
                  : 'Contacts, Violations…'
            }
            icon={<Cpu size={12} className="text-teal-600" />}
            iconBg="bg-white border border-teal-100"
            disabled={!canCreateForms}
            onClick={onAddModule}
          />
        )}
      </div>
      {!selectedFormIsModule && !selectedFormIsHeader && (
        <>
          <SectionHeader label="Sections" />
          <div className="py-1">
            <DraggableSectionItem onClick={onAddSection} />
            {activeFormName ? (
              <p className="text-[9px] text-gray-400 px-3 pb-1">Adds to: <span className="font-medium text-gray-500">{activeFormName}</span></p>
            ) : (
              <p className="text-[9px] text-amber-500 px-3 pb-1">Select a form first</p>
            )}
          </div>
        </>
      )}
      {selectedFormIsHeader && (
        <div className="px-3 py-2 bg-amber-50 border border-amber-100 rounded-lg mx-2 my-1">
          <p className="text-[10px] text-amber-600 font-medium">Header form selected</p>
          <p className="text-[10px] text-amber-500 mt-0.5 leading-relaxed">Edit the rich-text instructions directly on the canvas card. Sections and fields cannot be added to a header form.</p>
        </div>
      )}
    </div>
  );

  const moduleLockedNotice = (
    <div className="flex-1 flex flex-col items-center justify-center px-4 py-8 text-center gap-2">
      <div className="w-8 h-8 rounded-full bg-teal-50 flex items-center justify-center">
        <Cpu size={14} className="text-teal-500" />
      </div>
      <p className="text-[11px] font-semibold text-gray-600">System Module</p>
      <p className="text-[10px] text-gray-400 leading-relaxed">
        This form is a system module. Its layout and fields are defined by the module type and cannot be customised here.
      </p>
    </div>
  );

  const headerLockedNotice = (
    <div className="flex-1 flex flex-col items-center justify-center px-4 py-8 text-center gap-2">
      <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center">
        <FileText size={14} className="text-amber-500" />
      </div>
      <p className="text-[11px] font-semibold text-gray-600">Header Form</p>
      <p className="text-[10px] text-gray-400 leading-relaxed">
        Header forms contain rich-text instructions only. Use the editor on the canvas card to add formatted content.
      </p>
    </div>
  );

  const fieldsPanel = selectedFormIsModule ? moduleLockedNotice : selectedFormIsHeader ? headerLockedNotice : (
    <div className="flex-1 overflow-y-auto">
      {activeFormName ? (
        <div className="px-3 py-1.5 bg-blue-50 border-b border-blue-100">
          <p className="text-[9px] text-blue-600">Adding to: <span className="font-medium">{activeFormName}</span></p>
        </div>
      ) : (
        <div className="px-3 py-1.5 bg-amber-50 border-b border-amber-100">
          <p className="text-[9px] text-amber-600">Select a form to add fields</p>
        </div>
      )}
      {FIELD_TYPE_GROUPS.map((group) => (
        <FieldSubGroup
          key={group.label}
          label={group.label}
          types={group.types}
          onAddField={onAddField}
        />
      ))}
      {/* Section 2.4: Saved Widget Library group removed. */}
    </div>
  );

  const dataPanel = selectedFormIsModule ? moduleLockedNotice : selectedFormIsHeader ? headerLockedNotice : (
    <div className="flex-1 overflow-y-auto">
      {activeFormName ? (
        <div className="px-3 py-1.5 bg-blue-50 border-b border-blue-100">
          <p className="text-[9px] text-blue-600">Adding to: <span className="font-medium">{activeFormName}</span></p>
        </div>
      ) : (
        <div className="px-3 py-1.5 bg-amber-50 border-b border-amber-100">
          <p className="text-[9px] text-amber-600">Select a form to add widgets</p>
        </div>
      )}
      <WidgetPalette
        templateType={templateType}
        fields={activeFormFields}
        onAddWidget={onAddWidget}
        onAddWidgets={onAddWidgets}
      />
    </div>
  );

  const activePanel = activeTab === 'forms' ? formsPanel : activeTab === 'fields' ? fieldsPanel : dataPanel;

  const paletteContent = (
    <div className="flex h-full overflow-hidden bg-white">
      {/* Tab rail */}
      <TabRail active={activeTab} onChange={handleTabClick} />

      {/* Content panel */}
      {panelOpen && (
        <div className="flex flex-col flex-1 overflow-hidden border-r border-gray-200">
          {/* Panel header */}
          <div className="shrink-0 px-3 py-2 border-b bg-gray-50 flex items-center justify-between gap-2">
            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
              {activeTab === 'forms' ? 'Forms & Sections' : activeTab === 'fields' ? 'Fields' : 'Data Widgets'}
            </span>
            <button
              onClick={handleTogglePin}
              title={pinned ? 'Unpin' : 'Pin open'}
              className="p-1 rounded-sm text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors shrink-0"
            >
              {pinned ? <PinOff size={12} /> : <Pin size={12} />}
            </button>
          </div>
          {activePanel}
        </div>
      )}
    </div>
  );

  const sidebarContentWidth = panelOpen ? TAB_RAIL_W + width : TAB_RAIL_W;
  const totalWidth = panelOpen ? sidebarContentWidth + 24 : sidebarContentWidth;

  // ── PINNED: occupies layout space ──
  if (pinned) {
    return (
      <div className="flex shrink-0 h-full" style={{ width: totalWidth }}>
        <div className="flex h-full" style={{ width: totalWidth }}>
          <div className="h-full shrink-0 overflow-hidden" style={{ width: sidebarContentWidth }}>
            {paletteContent}
          </div>
          {panelOpen && <PaletteResizeHandle onResize={handleResize} />}
        </div>
      </div>
    );
  }

  // ── UNPINNED: tab rail always visible, panel overlays canvas ──
  return (
    <div className="relative w-8 shrink-0 h-full">
      {/* Always-visible tab rail */}
      <TabRail active={activeTab} onChange={handleTabClick} />
      {/* Fly-out content panel */}
      {panelOpen && (
        <div
          className="absolute left-full top-0 z-40 flex shadow-xl border-r border-gray-200 overflow-visible bg-white h-full"
          style={{ width: width + 24 }}
        >
          <div className="flex flex-col h-full overflow-hidden" style={{ width }}>
            <div className="shrink-0 px-3 py-2 border-b bg-gray-50 flex items-center justify-between gap-2">
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                {activeTab === 'forms' ? 'Forms & Sections' : activeTab === 'fields' ? 'Fields' : 'Data Widgets'}
              </span>
              <button
                onClick={handleTogglePin}
                title="Pin open"
                className="p-1 rounded-sm text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors shrink-0"
              >
                <Pin size={12} />
              </button>
            </div>
            {activePanel}
          </div>
          <PaletteResizeHandle onResize={handleResize} />
        </div>
      )}
    </div>
  );
}

export default CanvasFieldPalette;
